'use client';

import type { Variants } from 'motion/react';
import { motion } from 'motion/react';
import type * as React from 'react';
import { AppleLoginButton } from './apple-login-button';
import { GoogleLoginButton } from './google-login-button';
import { buttonVariants, headingVariants, paragraphVariants } from './helper';
import { LoginLogo } from './login-logo';
import { PasskeyLoginButton } from './passkey-login-button';
import { usePasskeyAvailable } from './use-passkey-available';

const secondButtonVariants: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: 1.7, ease: [0.16, 1, 0.3, 1] },
  },
};

interface ILoginCardContentProps {
  errorMessage?: string | null;
  onProviderStart?: (method: string) => void;
}

export function LoginCardContent({
  errorMessage,
  onProviderStart,
}: ILoginCardContentProps): React.ReactNode {
  const { isAvailable, isLoading } = usePasskeyAvailable();

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-6 md:p-8">
      {errorMessage && (
        <div className="w-full rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:border-red-800 dark:bg-red-950/80 dark:text-red-200">
          {errorMessage}
        </div>
      )}
      <LoginLogo />
      <motion.div
        className="flex flex-col items-center gap-2 text-center"
        variants={headingVariants}
        initial="hidden"
        animate="visible"
      >
        <h1 className="text-2xl font-bold text-foreground drop-shadow-sm">Welcome to Cool Mate</h1>
      </motion.div>
      <motion.div
        className="flex flex-col items-center gap-2 text-center"
        variants={paragraphVariants}
        initial="hidden"
        animate="visible"
      >
        <p className="text-foreground/90 dark:text-muted-foreground text-balance">
          Let&apos;s make today count
        </p>
      </motion.div>
      <div className="flex flex-col gap-3 items-center">
        <motion.div variants={buttonVariants} initial="hidden" animate="visible">
          <GoogleLoginButton onStart={() => onProviderStart?.('google')} />
        </motion.div>
        <motion.div variants={secondButtonVariants} initial="hidden" animate="visible">
          <AppleLoginButton onStart={() => onProviderStart?.('apple')} />
        </motion.div>
        {!isLoading && isAvailable && (
          <motion.div variants={secondButtonVariants} initial="hidden" animate="visible">
            <PasskeyLoginButton onStart={() => onProviderStart?.('passkey')} />
          </motion.div>
        )}
      </div>
    </div>
  );
}
